import * as React from "react";
import { useEffect, useState, ChangeEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import {
    Radio,
    RadioGroup,
    TextField,
    FormControl,
    FormControlLabel,
    FormLabel,
    Autocomplete,
    Button,
    Checkbox,
    Alert,
    CircularProgress,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useForm, Controller, FieldValues } from "react-hook-form";
import { getApiCall, postApiCall } from "../../../services/apiHelper";
import { ServiceName } from "../../../constants";
import { FundConfigModel } from "../../util/All_interfaces";
import { CurrentUser } from "../../../redux/slices/currentUserSlice";
import { useAppSelector } from "../../../redux/hooks";
import { element } from "prop-types";
import { UserContext, updateUserContext } from "../../../redux/slices/userContextSlice";
import { TaskmgmtServiceURL } from "../../../config/config";
import { ToastMessage } from "../../util/ToastMessage";
import { getProfile } from "../../../services/userContext";
import { useDispatch } from "react-redux";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const FundConfigurationDisplayEditForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const page = params.page;
    const userId = params.userRoleId;

    const [status, setStatus] = useState<string>("Active");
    const [valuationType, setValuationType] = useState<string>("false");
    const [regionList, setRegionList] = useState([{ title: "US", id: 1 }, { title: "EMEA", id: 2 }, { title: "APAC", id: 3 }]);
    const [fetchedRegionList, setFetchedRegionList] = useState<any>([]);
    const [pageType, setPageType] = useState<boolean>(false);
    const [workflow, setWorkflow] = useState<Array<{ id: number, name: string, version: string }>>([]);
    const [workFlowNames, setWorkflowNames] = useState<string[]>([]);
    const [postDataMessage, setPostDataMessage] = useState<{ code: number, message: string, isError: boolean }>({ code: 0, message: "", isError: false });
    const [loading, setLoading] = useState(false);
    const [selectedRegions, setSelectedRegions] = useState<any[]>([]);

    const [postFormdata, setPostFormData] = useState<FundConfigModel | any>({
        fundID: '',
        fundName: '',
        investmentType: '',
        appraisalSchedule: '',
        isFiduciaryApprovalRequired: false,
        delayPosting: '',
        scheduleDayAppraisal: '',
        scheduleDelayCount: 0,
        workflowtemplate: '',
        workflowVersion: '',
        region: '',
        isBlackOutRequired: false,
        isValuationTypeRequired: false,
        percentageChange: '',
        id: 0,
        createdBy: '',
        created: '',
        lastModifiedBy: '',
        lastModified: ''
    });

    const [postFormdataError, setPostFormdataError] = useState<any>({
        fundName: false,
        investmentType: false,
        appraisalSchedule: false,
        delayPosting: false,
        scheduleDayAppraisal: false,
        workflowtemplate: false,
        region: false,
        percentageChange: false
    });

    const currentUserData: CurrentUser = useAppSelector((state) => state.currentUser.data);
    const userContext: UserContext = useAppSelector((state) => state.UserContext.data);

    const handleStatusChange = (
        event: ChangeEvent<HTMLInputElement>,
        value: string
    ): void => {
        setStatus(value);
        setPostFormData({ ...postFormdata, isBlackOutRequired: value == "Active" ? true : false });
    };

    const handleValuationTypeChange = (
        event: ChangeEvent<HTMLInputElement>,
        value: string
    ): void => {
        setValuationType(value);
        setPostFormData({ ...postFormdata, isValuationTypeRequired: value == "Active" ? true : false });
    };

    const scheduleList = ["Quarterly", "Semi-Annually", "Annually", "Ad-Hoc"];
    const scheduleDay = ["1st Day of Month", "Last Day of Month"];
    const { control, handleSubmit } = useForm({});

    const regionHandler = (regionData: any) => {
        switch (regionData) {
            case 'US;EMEA':
                setFetchedRegionList([{ title: "US", id: 1 },
                { title: "EMEA", id: 2 }]);
                break;
            case 'US;APAC':
                setFetchedRegionList([{ title: "US", id: 1 },
                { title: "APAC", id: 3 }]);
                break;
            case 'US;EMEA;APAC':
                setFetchedRegionList([{ title: "US", id: 1 },
                { title: "EMEA", id: 2 },
                { title: "APAC", id: 3 }]);
                break;
            case 'EMEA;APAC':
                setFetchedRegionList([
                    { title: "EMEA", id: 2 },
                    { title: "APAC", id: 3 }]);
                break;
            case 'US':
                setFetchedRegionList([{ title: "US", id: 1 }]);
                break;
            case 'EMEA':
                setFetchedRegionList([{ title: "EMEA", id: 2 }]);
                break;
            case 'APAC':
                setFetchedRegionList([{ title: "APAC", id: 3 }]);
                break;
            case '':
                setFetchedRegionList([]);
                break;
        }

    }

    const fetchData = async () => {
        await getApiCall(`${TaskmgmtServiceURL}/FundConfigurations/getbyId/${params.fundID}`,
            ServiceName.TASKMANAGEMENTSERVICE).then((data: any) => {
                var resultData = data.result;

                if (resultData.region != null || resultData.region == '')
                    regionHandler(resultData.region);

                setPostFormData({
                    ...postFormdata,
                    appraisalSchedule: resultData.appraisalSchedule,
                    created: resultData.created,
                    id: resultData.id,
                    fundName: resultData.fundName,
                    createdBy: resultData.createdBy,
                    delayPosting: resultData.delayPosting,
                    fundID: resultData.fundID,
                    investmentType: resultData.investmentType,
                    isBlackOutRequired: resultData.isBlackOutRequired,
                    isValuationTypeRequired: resultData.isValuationTypeRequired,
                    isFiduciaryApprovalRequired: resultData.isFiduciaryApprovalRequired,
                    lastModified: resultData.lastModified,
                    lastModifiedBy: resultData.lastModifiedBy,
                    percentageChange: resultData.percentageChange,
                    region: (resultData.region == null ? '' : resultData.region),
                    scheduleDayAppraisal: resultData.scheduleDayAppraisal,
                    workflowtemplate: resultData.workflowTemplate,
                    workflowVersion: resultData.workflowVersion
                })

            });

        await getApiCall(`${TaskmgmtServiceURL}/WorkflowTemplates/GetAll`,
            ServiceName.TASKMANAGEMENTSERVICE).then((data: any) => {
                var workFlowNameList: string[] = [];
                data.result.forEach((element: { id: number, name: string, version: string }) => {
                    workFlowNameList.push(element.name);
                });
                setWorkflowNames(workFlowNameList);
                setWorkflow(data.result);
                setLoading(false);
            });
    }

    useEffect(() => {
        if (Object.keys(userContext).length == 0) {
            getProfile(true).then((data: any) => {
                dispatch(updateUserContext(data));
            });
        }
    }, []);

    useEffect(() => {
        setPageType(page == "view" ? true : false);
        setLoading(true);
        if (Object.keys(userContext).length != 0) {
            fetchData();

            var regionContext = userContext.region;
            if (regionContext != '') {
                if (regionContext == 'US;EMEA') {
                    setRegionList(
                        [{ title: "US", id: 1 },
                        { title: "EMEA", id: 2 }])
                }
                else if (regionContext == 'US;APAC') {
                    setRegionList(
                        [{ title: "US", id: 1 },
                        { title: "APAC", id: 2 }])
                }
                else if (regionContext == 'EMEA;APAC') {
                    setRegionList(
                        [{ title: "EMEA", id: 1 },
                        { title: "APAC", id: 2 }])
                }
            }

        }

    }, [userContext]);

    const handleRegionChangeAndDateTime = (value: any) => {
        let today = new Date();
        let date = today.getDate() + "-" + today.getMonth() + 1 + "-" + today.getFullYear() + " " + today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds();
        var selectedRegions = '';

        if (value.length == 0) {
            selectedRegions = '';
            setFetchedRegionList([]);
        } else {
            var repeatedRegions = ''
            value.forEach((element: any, index: any) => {
                for (let i = index + 1; i < value.length; i++) {
                    if (element.title === value[i].title)
                        repeatedRegions = value[i].title;
                }
            });

            var filteredRegionValues = value.filter((elem: { title: string; }) => elem.title !== repeatedRegions);
            var tempRegions: any[] = [];
            filteredRegionValues.forEach((element: { title: any; }) => {
                tempRegions.push(element.title);
            });
            var sortedTempRegions = tempRegions.sort().reverse();
            var filteredSortedTempRegions = sortedTempRegions.filter((value, index) => sortedTempRegions.indexOf(value) === index)
            filteredSortedTempRegions.forEach((element: any) => {
                if (selectedRegions == '') {
                    selectedRegions = element;
                } else {
                    selectedRegions = selectedRegions + ";" + element;
                }
            });

        }

        if (selectedRegions != null)
            regionHandler(selectedRegions);
        else
            setFetchedRegionList([]);



        setPostFormData({
            ...postFormdata, region: selectedRegions,
            lastModified: date, lastModifiedBy: currentUserData.email
        });
    }

    const submitForm = async () => {

        setLoading(true);
        if (postFormdata.fundName.length === 0 ||
            postFormdata.investmentType.length === 0 ||
            postFormdata.appraisalSchedule.length === 0 ||
            postFormdata.delayPosting.length === 0 ||
            postFormdata.scheduleDayAppraisal.length === 0 ||
            postFormdata.workflowtemplate.length === 0 ||
            postFormdata.region.length === 0 ||
            postFormdata.percentageChange.length === 0) {
            setLoading(false);
            setPostDataMessage({ code: 0, message: "Please correct the validation errors!", isError: true });
        } else {

            await postApiCall(`${TaskmgmtServiceURL}/FundConfigurations/Update`, postFormdata, ServiceName.TASKMANAGEMENTSERVICE).then((data: any) => {
                setLoading(false);
                setPostDataMessage({ code: 1, message: `${postFormdata.fundID} Configuration record is successfully submitted`, isError: false });
                setTimeout(() => {
                    navigate(`/fundconfig/list/`)
                }
                    , 3000);

            }).catch(e => {
                setPostDataMessage({ code: e.response.status, message: e.response.data.responseException.exceptionMessage, isError: e.response.data.isError })
                setLoading(false);
            });
        }
    }

    return (
        <div className="form-wrap">
            <Box sx={{ width: "100%", typography: "body1" }}>
                {loading ? (
                    <div className="overlaySpinner">
                        <CircularProgress />
                    </div>
                ) : null}
                {postDataMessage.isError ?
                    <ToastMessage message={postDataMessage.message} severity="error" />
                    : null}
                {postDataMessage.code == 1 ?
                    <ToastMessage message={postDataMessage.message} severity="success" />
                    : null}
                <Grid container spacing={3}>
                    <Grid item xs={6} sm={6} >
                        <TextField
                            {...params}
                            size="small"
                            label="Fund"
                            required
                            InputLabelProps={{
                                sx: {
                                    '& .MuiInputLabel-asterisk': {
                                        color: 'red',
                                    },
                                },
                            }}
                            inputProps={{
                                readOnly: true
                            }}
                            value={postFormdata.fundName}
                            style={{ width: "100%" }}
                            className={!pageType ? "Autopopulatefield" : ''}
                        />
                    </Grid>
                    <Grid item xs={6} sm={6}>
                        <TextField
                            {...params}
                            size="small"
                            required
                            InputLabelProps={{
                                sx: {
                                    '& .MuiInputLabel-asterisk': {
                                        color: 'red',
                                    },
                                },
                            }}
                            label="Investment Type"
                            inputProps={{
                                readOnly: true
                            }}
                            value={postFormdata.investmentType}
                            style={{ width: "100%" }}
                            className={!pageType ? "Autopopulatefield" : ''}
                        />
                    </Grid>
                    <Grid item xs={6} sm={6}>
                        {pageType ? <TextField label="Schedule" required
                            InputLabelProps={{
                                sx: {
                                    '& .MuiInputLabel-asterisk': {
                                        color: 'red',
                                    },
                                },
                            }} inputProps={{
                                readOnly: true
                            }}
                            value={postFormdata.appraisalSchedule}
                            style={{ width: "100%" }}
                            size="small"
                        /> : <Controller
                            name="Schedule"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    id="fund-id"
                                    options={scheduleList}
                                    value={postFormdata.appraisalSchedule}
                                    onChange={(event: any, value: any) => {
                                        if (value == null) {
                                            setPostFormData({ ...postFormdata, appraisalSchedule: '' });
                                            setPostFormdataError({ ...postFormdataError, appraisalSchedule: true })
                                        } else {
                                            setPostFormData({ ...postFormdata, appraisalSchedule: value });
                                            setPostFormdataError({ ...postFormdataError, appraisalSchedule: false })
                                        }

                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Schedule"
                                            size="small"
                                            required
                                            InputLabelProps={{
                                                sx: {
                                                    '& .MuiInputLabel-asterisk': {
                                                        color: 'red',
                                                    },
                                                },
                                            }}
                                            error={postFormdataError.appraisalSchedule}
                                        />
                                    )}
                                />
                            )}
                        />}

                    </Grid>
                    <Grid item xs={6} sm={6}>
                        {pageType ? <TextField
                            id="outlined-number"
                            label="Percentage Change"
                            size="small"
                            required
                            InputLabelProps={{
                                sx: {
                                    '& .MuiInputLabel-asterisk': {
                                        color: 'red',
                                    },
                                },
                            }}
                            value={postFormdata.percentageChange}
                        /> : <Controller
                            name="PercentageChange"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    id="outlined-number"
                                    label="Percentage Change"
                                    type="number"
                                    size="small"
                                    required
                                    InputLabelProps={{
                                        sx: {
                                            '& .MuiInputLabel-asterisk': {
                                                color: 'red',
                                            },
                                        },
                                    }}
                                    value={postFormdata.percentageChange}
                                    onChange={(event: any) => {
                                        setPostFormData({ ...postFormdata, percentageChange: event.target.value });
                                        setPostFormdataError({ ...postFormdataError, percentageChange: event.target.value.length == 0 });
                                    }}
                                    error={postFormdataError.percentageChange}
                                />
                            )}
                        />}

                    </Grid>
                    <Grid item xs={6} sm={6}>
                        <FormControl
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "flex-start",
                                alignItems: "center",
                            }}
                        >
                            <FormLabel id="row-radio-buttons-group-label">
                                Is Blackout Period Required
                            </FormLabel>
                            <RadioGroup
                                row
                                style={{ marginLeft: "25px" }}
                                aria-labelledby="row-radio-buttons-group-label"
                                name="row-radio-buttons-group"
                                value={postFormdata.isBlackOutRequired == true ? "Active" : "InActive"}
                                onChange={handleStatusChange}

                            >
                                <FormControlLabel
                                    value="Active"
                                    control={<Radio />}
                                    label="Yes"
                                    disabled={pageType}
                                />
                                <FormControlLabel
                                    value="InActive"
                                    control={<Radio />}
                                    label="No"
                                    disabled={pageType}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                        <FormControl
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "flex-start",
                                alignItems: "center",
                            }}
                        >
                            <FormLabel id="row-radio-buttons-group-label">
                                Is Valuation Type Required
                            </FormLabel>
                            <RadioGroup
                                row
                                style={{ marginLeft: "25px" }}
                                aria-labelledby="row-radio-buttons-group-label"
                                name="row-radio-buttons-group"
                                value={postFormdata.isValuationTypeRequired == true ? "Active" : "InActive"}
                                onChange={handleValuationTypeChange}

                            >
                                <FormControlLabel
                                    value="Active"
                                    control={<Radio />}
                                    label="Yes"
                                    disabled={pageType}
                                />
                                <FormControlLabel
                                    value="InActive"
                                    control={<Radio />}
                                    label="No"
                                    disabled={pageType}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={6}>
                        {pageType ? <TextField
                            id="outlined-number"
                            label="Delay Posting"
                            size="small"
                            required
                            InputLabelProps={{
                                sx: {
                                    '& .MuiInputLabel-asterisk': {
                                        color: 'red',
                                    },
                                },
                            }}
                            value={postFormdata.delayPosting}
                        /> : <Controller
                            name="DelayPosting"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    id="outlined-number"
                                    label="Delay Posting"
                                    type="number"
                                    size="small"
                                    required
                                    InputLabelProps={{
                                        sx: {
                                            '& .MuiInputLabel-asterisk': {
                                                color: 'red',
                                            },
                                        },
                                    }}
                                    value={postFormdata.delayPosting}
                                    onChange={(event: any) => {
                                        setPostFormData({ ...postFormdata, delayPosting: event.target.value });
                                        setPostFormdataError({ ...postFormdataError, delayPosting: event.target.value.length == 0 });
                                    }}
                                    error={postFormdataError.delayPosting}
                                />
                            )}
                        />}

                    </Grid>
                    <Grid item xs={6} sm={6}>
                        {pageType ? <TextField
                            {...params}
                            label="Schedule Day"
                            style={{ width: "100%" }}
                            size="small"
                            required
                            InputLabelProps={{
                                sx: {
                                    '& .MuiInputLabel-asterisk': {
                                        color: 'red',
                                    },
                                },
                            }}
                            value={postFormdata.scheduleDayAppraisal == "01" ?
                                scheduleDay[0] : scheduleDay[1]}
                        /> : <Controller
                            name="ScheduleDay"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    id="fund-id"
                                    options={scheduleDay}
                                    value={postFormdata.scheduleDayAppraisal}
                                    onChange={(event: any, value: any) => {
                                        if (value == null) {
                                            setPostFormData({ ...postFormdata, scheduleDayAppraisal: '' });
                                            setPostFormdataError({ ...postFormdataError, scheduleDayAppraisal: true });
                                        } else {
                                            setPostFormData({ ...postFormdata, scheduleDayAppraisal: value });
                                            setPostFormdataError({ ...postFormdataError, scheduleDayAppraisal: false || value.length == 0 });
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Schedule Day"
                                            size="small"
                                            required
                                            InputLabelProps={{
                                                sx: {
                                                    '& .MuiInputLabel-asterisk': {
                                                        color: 'red',
                                                    },
                                                },
                                            }}
                                            error={postFormdataError.scheduleDayAppraisal}
                                        />
                                    )}
                                />
                            )}
                        />}

                    </Grid>
                    <Grid item xs={6} sm={6}>
                        {pageType ? <TextField
                            {...params}
                            label="Workflow Template"
                            style={{ width: "100%" }}
                            size="small"
                            required
                            InputLabelProps={{
                                sx: {
                                    '& .MuiInputLabel-asterisk': {
                                        color: 'red',
                                    },
                                },
                            }}
                            value={postFormdata.workflowtemplate}
                        /> : <Controller
                            name="Workflow Template"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    id="fund-id"
                                    options={workFlowNames}
                                    value={postFormdata.workflowtemplate}

                                    onChange={(event, value) => {
                                        console.log(value);

                                        var filteredWorkflow = workflow.filter((element) => element.name == value);
                                        if (value != null) {
                                            setPostFormData({
                                                ...postFormdata,
                                                workflowtemplate: value,
                                                workflowVersion: filteredWorkflow[0].version
                                            });
                                            setPostFormdataError({ ...postFormdataError, workflowtemplate: false || value.length == 0 });
                                        } else {
                                            setPostFormData({ ...postFormdata, workflowtemplate: '', workflowVersion: '' });
                                            setPostFormdataError({ ...postFormdataError, workflowtemplate: true });
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Workflow Template"
                                            size="small"
                                            required
                                            InputLabelProps={{
                                                sx: {
                                                    '& .MuiInputLabel-asterisk': {
                                                        color: 'red',
                                                    },
                                                },
                                            }}
                                            error={postFormdataError.workflowtemplate}
                                        />
                                    )}
                                />
                            )}
                        />}

                    </Grid>
                    <Grid item xs={6} sm={6}>
                        <TextField
                            required
                            InputLabelProps={{
                                sx: {
                                    '& .MuiInputLabel-asterisk': {
                                        color: 'red',
                                    },
                                },
                            }}
                            label="Workflow Version"
                            variant="outlined"
                            size="small"
                            value={postFormdata.workflowVersion}
                            fullWidth
                            className="Autopopulatefield"
                        />
                    </Grid>

                    <Grid item xs={6} sm={6}>
                        {pageType ? <TextField
                            {...params}
                            label="Region"
                            style={{ width: "100%" }}
                            size="small"
                            required
                            InputLabelProps={{
                                sx: {
                                    '& .MuiInputLabel-asterisk': {
                                        color: 'red',
                                    },
                                },
                            }}
                            value={postFormdata.region}
                        /> : <Controller
                            name="Region"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    multiple
                                    id="checkboxes-tags-demo"
                                    options={regionList}
                                    disableCloseOnSelect
                                    getOptionLabel={(option) => option.title}
                                    onChange={(event, value) => {
                                        handleRegionChangeAndDateTime(value);
                                        setPostFormdataError({ ...postFormdataError, region: false || value.length == 0 });
                                    }}
                                    value={fetchedRegionList}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props}>
                                            <Checkbox
                                                icon={icon}
                                                checkedIcon={checkedIcon}
                                                style={{ marginRight: 8 }}
                                                checked={postFormdata.region.includes(option.title)}
                                            />
                                            {option.title}
                                        </li>
                                    )}
                                    // style={{ width: 500 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Region"
                                            size="small"
                                            required
                                            InputLabelProps={{
                                                sx: {
                                                    '& .MuiInputLabel-asterisk': {
                                                        color: 'red',
                                                    },
                                                },
                                            }}
                                            error={postFormdataError.region}
                                        />
                                    )}
                                />
                            )}
                        />}

                    </Grid>
                    <Grid item xs={12} sm={12}>
                        {pageType ? <Button sx={{ textTransform: "none" }} variant="contained" size="medium" color="success"
                            onClick={() => { navigate(`/fundconfig/edit/${params.fundID}`); setPageType(false); }}
                        >
                            Edit
                        </Button> : <Button sx={{ textTransform: "none" }} variant="contained" size="medium" color="success"
                            onClick={() => {
                                submitForm();
                                setPostFormdataError({
                                    ...postFormdataError,
                                    fundName: postFormdata.fundName.length === 0,
                                    investmentType: postFormdata.investmentType.length === 0,
                                    appraisalSchedule: postFormdata.appraisalSchedule.length === 0,
                                    delayPosting: postFormdata.delayPosting.length === 0,
                                    scheduleDayAppraisal: postFormdata.scheduleDayAppraisal.length === 0,
                                    workflowtemplate: postFormdata.workflowtemplate.length === 0,
                                    region: postFormdata.region.length === 0,
                                    percentageChange: postFormdata.percentageChange.length === 0
                                });
                            }} >
                            Submit
                        </Button>}
                        <Button sx={{ textTransform: "none", ml: 2 }}
                            variant="outlined"
                            size="medium"
                            color="error"
                            onClick={() => navigate("/fundconfig/list")}
                        >
                            {pageType ? "Close" : "Cancel"}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </div>
    );
};
